import { useEffect, useMemo, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  MenuItem,
  Badge,
  Paper,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";
import LeftMenu from "../menus/LeftMenu";
import HeaderBar from "../headers/HeaderBar";
import { useAuth } from "../../context/AuthContext";
import { LanguageContext } from "../../context/LanguageContext";
import AgentConversationsDialog from "./AgentConversationsDialog";
import AgentTestDialog from "./AgentTestDialog";
import AgentIntegrationsDialog from "./AgentIntegrationsDialog";
import { todayKey } from "../dateKeys";
// Icons for tone selector
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import TonalityIcon from "@mui/icons-material/Tonality";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ScienceIcon from "@mui/icons-material/Science";
import ForumIcon from "@mui/icons-material/Forum";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HubIcon from "@mui/icons-material/Hub";
import MarkUnreadChatAltIcon from "@mui/icons-material/MarkUnreadChatAlt";
// Icons for overview chips

import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BlockIcon from "@mui/icons-material/Block";

export default function AgentDetailDesktop() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { translate } = useContext(LanguageContext);

  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [agent, setAgent] = useState(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [guidelines, setGuidelines] = useState("");
  const [tone, setTone] = useState("");
  const [forbiddenTopics, setForbiddenTopics] = useState("");
  const [publicEnabled, setPublicEnabled] = useState(true);

  // Stats
  const [convTotal, setConvTotal] = useState(0);
  const [convEmail, setConvEmail] = useState(0);
  const [convNoEmail, setConvNoEmail] = useState(0);
  const [interactions, setInteractions] = useState(0);
  const [convIds, setConvIds] = useState([]);
  const [newConvCount, setNewConvCount] = useState(0);
  const [monthlyTokens, setMonthlyTokens] = useState(0);
  const [monthlyCostUsd, setMonthlyCostUsd] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [monthlyHistory, setMonthlyHistory] = useState([]);

  // Dialogs
  const [showConvos, setShowConvos] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);

  const CONTEXT_TOKEN_LIMIT = 5000;
  const estimateTokens = (s) => Math.ceil(String(s || "").length / 4);
  const ctxTokens = useMemo(
    () =>
      estimateTokens(guidelines) +
      estimateTokens(tone) +
      estimateTokens(forbiddenTopics),
    [guidelines, tone, forbiddenTopics]
  );

  // Resolve tenantId from user profile
  useEffect(() => {
    (async () => {
      try {
        if (!currentUser?.uid) return;
        const prof = await getDoc(doc(db, "profilsUsers", currentUser.uid));
        const tId = prof.exists() ? prof.data()?.tenantId || null : null;
        setTenantId(tId);
      } catch {}
    })();
  }, [currentUser?.uid]);

  // Load agent
  useEffect(() => {
    if (!tenantId || !agentId) return;
    setLoading(true);
    (async () => {
      try {
        const ref = doc(db, "tenants", tenantId, "agents", agentId);
        const snap = await getDoc(ref);
        const a = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setAgent(a);
        setName(a?.name || "");
        setDescription(a?.description || "");
        setGuidelines(a?.guidelines || "");
        setTone(a?.tone || "");
        setForbiddenTopics(a?.forbiddenTopics || "");
        setPublicEnabled(a?.publicEnabled !== false);
      } catch (e) {
        setErr("Failed to load agent");
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, agentId]);

  // Live stats (conversations + interactions)
  useEffect(() => {
    if (!agentId) return;
    try {
      const ref = collection(
        db,
        "messages",
        `agent_${agentId}`,
        "conversations"
      );
      const unsub = onSnapshot(ref, (snap) => {
        let total = 0;
        let withEmail = 0;
        let withoutEmail = 0;
        let inter = 0;
        const ids = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          ids.push(d.id);
          total += 1;
          const it = Number(data.interactions || 0);
          inter += isFinite(it) ? it : 0;
          const email = data?.userHints?.email;
          if (email && String(email).trim()) withEmail += 1;
          else withoutEmail += 1;
        });
        setConvTotal(total);
        setConvEmail(withEmail);
        setConvNoEmail(withoutEmail);
        setInteractions(inter);
        setConvIds(ids);
        try {
          const seenKey = tenantId
            ? `tm:seenConvos:${tenantId}:${agentId}`
            : `tm:seenConvos:${agentId}`;
          const seenArr = JSON.parse(localStorage.getItem(seenKey) || "[]");
          const seen = new Set(Array.isArray(seenArr) ? seenArr : []);
          const unseen = ids.filter((id) => !seen.has(id)).length;
          setNewConvCount(unseen);
        } catch {}
      });
      return unsub;
    } catch {}
  }, [agentId, tenantId]);

  const markConversationSeen = (conversationId) => {
    try {
      const seenKey = tenantId
        ? `tm:seenConvos:${tenantId}:${agentId}`
        : `tm:seenConvos:${agentId}`;
      const prev = JSON.parse(localStorage.getItem(seenKey) || "[]");
      const set = new Set(Array.isArray(prev) ? prev : []);
      set.add(conversationId);
      const arr = Array.from(set);
      localStorage.setItem(seenKey, JSON.stringify(arr));
      const unseen = convIds.filter((id) => !set.has(id)).length;
      setNewConvCount(unseen);
    } catch {}
  };

  // Monthly usage from usage_daily (client aggregation)
  const monthPrefix = useMemo(() => todayKey().slice(0, 7), []);
  useEffect(() => {
    if (!tenantId || !agentId) return;
    const usageRef = collection(
      db,
      "tenants",
      tenantId,
      "agents",
      agentId,
      "usage_daily"
    );
    try {
      const unsub = onSnapshot(usageRef, (snap) => {
        let tokens = 0;
        let cost = 0;
        snap.forEach((docSnap) => {
          const id = docSnap.id || "";
          const data = docSnap.data() || {};
          if (id.startsWith(monthPrefix)) {
            tokens += Number(data.totalTokens || 0) || 0;
            cost += Number(data.costUsd || 0) || 0;
          }
        });
        setMonthlyTokens(tokens);
        setMonthlyCostUsd(cost);
      });
      return unsub;
    } catch {}
  }, [tenantId, agentId, monthPrefix]);

  // Load previous months history when modal opens
  useEffect(() => {
    if (!showHistory || !tenantId || !agentId) return;
    (async () => {
      setHistoryLoading(true);
      try {
        const usageRef = collection(
          db,
          "tenants",
          tenantId,
          "agents",
          agentId,
          "usage_daily"
        );
        const snap = await getDocs(usageRef);
        const byMonth = new Map();
        snap.forEach((d) => {
          const data = d.data() || {};
          const key = (data.dayKey || d.id || "").slice(0, 7); // YYYY-MM
          if (!key) return;
          const prev = byMonth.get(key) || { totalTokens: 0, costUsd: 0 };
          prev.totalTokens += Number(data.totalTokens || 0) || 0;
          prev.costUsd += Number(data.costUsd || 0) || 0;
          byMonth.set(key, prev);
        });
        let arr = Array.from(byMonth.entries())
          .map(([month, agg]) => ({ month, ...agg }))
          .sort((a, b) => (a.month < b.month ? 1 : -1));
        // Exclude current month to focus on previous months
        arr = arr.filter((m) => m.month !== monthPrefix);
        setMonthlyHistory(arr);
      } catch (e) {
        console.error("[AgentDetail] load monthly history failed", e);
        setMonthlyHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    })();
  }, [showHistory, tenantId, agentId, monthPrefix]);

  // Format helper for month labels like "March 2025"
  const formatMonthLabel = (ym) => {
    try {
      const [y, m] = String(ym || "").split("-");
      const d = new Date(Number(y), Number(m) - 1, 1);
      return d.toLocaleString(undefined, { month: "long", year: "numeric" });
    } catch {
      return ym;
    }
  };

  const monthlyBudgetUsd = 5;
  const pct = Math.max(
    0,
    Math.min(100, (monthlyCostUsd / monthlyBudgetUsd) * 100)
  );
  const hue = Math.max(0, 140 - pct * 1.4);
  const gaugeColor = `hsl(${hue}, 80%, 45%)`;

  const canSave = useMemo(
    () =>
      Boolean(tenantId && agentId) &&
      name.trim().length > 0 &&
      !saving &&
      ctxTokens <= CONTEXT_TOKEN_LIMIT,
    [tenantId, agentId, name, saving, ctxTokens]
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setErr("");
    try {
      const ref = doc(db, "tenants", tenantId, "agents", agentId);
      await updateDoc(ref, {
        name: name.trim(),
        description: description.trim(),
        guidelines: guidelines.trim(),
        tone: tone.trim(),
        forbiddenTopics: forbiddenTopics.trim(),
        publicEnabled: Boolean(publicEnabled),
        updatedAt: new Date(),
      });
      navigate("/app/agents");
    } catch (e) {
      setErr("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublic = async (checked) => {
    try {
      if (!tenantId || !agentId) return;
      const ref = doc(db, "tenants", tenantId, "agents", agentId);
      await updateDoc(ref, { publicEnabled: checked });
      setPublicEnabled(checked);
    } catch (e) {
      console.error("[AgentDetail] toggle publicEnabled failed", e);
    }
  };

  // Predefined tone choices with icon + color
  const toneOptions = [
    {
      value: "friendly",
      labelKey: "ToneFriendly",
      Icon: SentimentSatisfiedAltIcon,
      color: "#00E676",
    },
    {
      value: "professional",
      labelKey: "ToneProfessional",
      Icon: WorkOutlineIcon,
      color: "#64B5F6",
    },
    {
      value: "concise",
      labelKey: "ToneConcise",
      Icon: BoltIcon,
      color: "#FFD54F",
    },
    {
      value: "playful",
      labelKey: "TonePlayful",
      Icon: EmojiEmotionsIcon,
      color: "#FF80AB",
    },
    {
      value: "supportive",
      labelKey: "ToneSupportive",
      Icon: VolunteerActivismIcon,
      color: "#BA68C8",
    },
    {
      value: "empathetic",
      labelKey: "ToneEmpathetic",
      Icon: FavoriteBorderIcon,
      color: "#FF8A65",
    },
    {
      value: "technical",
      labelKey: "ToneTechnical",
      Icon: ScienceIcon,
      color: "#26C6DA",
    },
    {
      value: "neutral",
      labelKey: "ToneNeutral",
      Icon: TonalityIcon,
      color: "#90A4AE",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <LeftMenu />
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <HeaderBar
          title={agent?.name || translate("Agents") || "Agent"}
          subtitle={translate("ManageAndMonitorAgents")}
          showProjectSelect={false}
        />

        {loading ? (
          <Box sx={{ p: 4, display: "flex", gap: 1, alignItems: "center" }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
            <Paper
              sx={{
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {err && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {err}
                </Alert>
              )}

              <Stack
                spacing={2}
                sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
              >
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {/* Left: name + description (30%) */}
                  <Box
                    sx={{
                      width: {
                        xs: "100%",
                        md: "30%",
                        border: "1px solid lightgrey",
                        paddingTop: "20px",
                        paddingBottom: "20px",
                        paddingLeft: "20px",
                        paddingRight: "20px",
                        borderRadius: "10px",
                      },
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        label="Agent Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                      />
                      <Tooltip
                        title={
                          publicEnabled
                            ? translate("ClickToDeactivate") ||
                              "Click to deactivate"
                            : translate("ClickToActivate") ||
                              "Click to activate"
                        }
                        arrow
                      >
                        <Chip
                          size="small"
                          clickable
                          onClick={() => togglePublic(!publicEnabled)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              togglePublic(!publicEnabled);
                            }
                          }}
                          label={
                            publicEnabled === false
                              ? translate("Inactive") || "Inactive"
                              : translate("Active") || "Active"
                          }
                          role="switch"
                          aria-checked={publicEnabled === true}
                          sx={{
                            backgroundColor:
                              publicEnabled === false ? "#3B0A0A" : "#003B2E",
                            color:
                              publicEnabled === false ? "#FF5252" : "#00E676",
                            fontWeight: 600,
                            border: "none",
                            px: 1.4,
                            letterSpacing: 0.3,
                            flexShrink: 0,
                            cursor: "pointer",
                            transition: "all .2s ease",
                            "&:hover": { filter: "brightness(1.1)" },
                            "&:active": { transform: "scale(0.98)" },
                            "&:focus-visible": {
                              outline: "2px solid rgba(0,230,118,0.6)",
                              outlineOffset: 2,
                            },
                          }}
                        />
                      </Tooltip>
                    </Stack>
                    <TextField
                      label={translate("AgentPresentationLabel") || "Agent Presentation to Client"}
                      placeholder="What this agent does in one or two sentences."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                    />
                    <TextField
                      select
                      label={translate("ToneLabel") || "Tone"}
                      value={tone || ""}
                      onChange={(e) => setTone(e.target.value)}
                      fullWidth
                      size="small"
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          const opt = toneOptions.find(
                            (o) => o.value === selected
                          );
                          if (!selected) {
                            return (
                              <span style={{ color: "rgba(255,255,255,0.6)" }}>
                                {translate("SelectTone") || "Select tone"}
                              </span>
                            );
                          }
                          const IconCmp = opt?.Icon || TonalityIcon;
                          const color = opt?.color || "#90A4AE";
                          const label =
                            (opt?.labelKey && translate(opt.labelKey)) ||
                            String(selected);
                          return (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <IconCmp sx={{ color, fontSize: 18 }} />
                              <span>{label}</span>
                            </Box>
                          );
                        },
                      }}
                    >
                      <MenuItem value="">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <TonalityIcon sx={{ color: "#90A4AE" }} />
                          <Typography sx={{ color: "text.secondary" }}>
                            {translate("SelectTone") || "Select tone"}
                          </Typography>
                        </Box>
                      </MenuItem>
                      {toneOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <opt.Icon sx={{ color: opt.color }} />
                            <Typography>
                              {(opt.labelKey && translate(opt.labelKey)) ||
                                opt.value}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  {/* Right: stats + usage + actions */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 280,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "text.secondary", fontSize: "24px" }}
                    >
                      {translate("OverviewTitle") || "Overview"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      alignItems="center"
                      sx={{ mb: 2, gap: 1.5 }}
                    >
                      <Chip
                        size="small"
                        icon={<ForumIcon />}
                        label={`${
                          translate("Conversations") || "Conversations"
                        }: ${convTotal}`}
                        sx={{
                          backgroundColor: "rgba(0,230,118,0.12)",
                          color: "#00E676",
                          fontWeight: 600,
                          "& .MuiChip-icon": { color: "#00E676" },
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<MailOutlineIcon />}
                        label={`${
                          translate("WithEmail") || "With email"
                        }: ${convEmail}`}
                        sx={{
                          backgroundColor: "rgba(100,181,246,0.15)",
                          color: "#64B5F6",
                          fontWeight: 600,
                          "& .MuiChip-icon": { color: "#64B5F6" },
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<BlockIcon />}
                        label={`${
                          translate("NoEmail") || "No email"
                        }: ${convNoEmail}`}
                        sx={{
                          backgroundColor: "rgba(255,82,82,0.15)",
                          color: "#FF5252",
                          fontWeight: 600,
                          "& .MuiChip-icon": { color: "#FF5252" },
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<BoltIcon />}
                        label={`${
                          translate("Interactions") || "Interactions"
                        }: ${interactions}`}
                        sx={{
                          backgroundColor: "rgba(255,213,79,0.18)",
                          color: "#FFD54F",
                          fontWeight: 600,
                          "& .MuiChip-icon": { color: "#FFD54F" },
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<MarkUnreadChatAltIcon />}
                        label={`${
                          translate("UnreadConversations") ||
                          "Unread conversations"
                        }: ${newConvCount}`}
                        sx={{
                          backgroundColor: "rgba(186,104,200,0.18)",
                          color: "#BA68C8",
                          fontWeight: 600,
                          "& .MuiChip-icon": { color: "#BA68C8" },
                        }}
                      />
                    </Stack>
                    {/* Progress moved to dedicated right box */}
                    <Stack direction="row" spacing={1.6} sx={{ mt: "auto" }}>
                      <Badge
                        badgeContent={newConvCount}
                        overlap="rectangular"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: "#00E676",
                            color: "#003B2E",
                            fontWeight: 700,
                            border: "1px solid #003B2E",
                            minWidth: 20,
                            height: 20,
                          },
                        }}
                      >
                        <Button
                          variant="contained"
                          onClick={() => {
                            setShowConvos(true);
                          }}
                          sx={{
                            backgroundColor: "#003B2E",
                            color: "#FFFFFF",
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: "10px",
                            px: 2.4,
                            boxShadow: "none",
                            "&:hover": { backgroundColor: "#005442" },
                          }}
                          startIcon={<ForumIcon />}
                        >
                          {translate("Conversations") || "Conversations"}
                        </Button>
                      </Badge>
                      <Button
                        variant="outlined"
                        onClick={() => setShowTest(true)}
                        sx={{
                          borderColor: "#616161",
                          color: "#E0E0E0",
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "8px",
                          px: 2,
                          "&:hover": {
                            borderColor: "#9E9E9E",
                            backgroundColor: "rgba(255,255,255,0.04)",
                          },
                        }}
                        startIcon={<SmartToyIcon />}
                      >
                        {translate("TestChat") || "Test chat"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowIntegrations(true)}
                        sx={{
                          borderColor: "#616161",
                          color: "#E0E0E0",
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "8px",
                          px: 2,
                          "&:hover": {
                            borderColor: "#9E9E9E",
                            backgroundColor: "rgba(255,255,255,0.04)",
                          },
                        }}
                        startIcon={<HubIcon />}
                      >
                        {translate("Integrations") || "Integrations"}
                      </Button>
                      {null}
                    </Stack>
                  </Box>

                  {/* Far Right: centered monthly usage progress */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{ position: "relative", display: "inline-flex" }}
                      >
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={140}
                          thickness={6}
                          sx={{ color: "action.disabledBackground" }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={pct}
                          size={140}
                          thickness={6}
                          sx={{
                            position: "absolute",
                            left: 0,
                            color: gaugeColor,
                            filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.25))",
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            fontSize={22}
                          >
                            {Math.round(pct)}%
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ minWidth: 0, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                          {translate("ThisMonth") || "This month"}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {new Intl.NumberFormat().format(
                            Math.round(monthlyTokens)
                          )}{" "}
                          tokens
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: "text.disabled", ml: 0.5 }}
                          >
                            {pct.toFixed(1)}%
                          </Typography>
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setShowHistory(true)}
                          sx={{ mt: 1.2, textTransform: "none" }}
                        >
                          {translate("PreviousMonths") || "Previous months"}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Alert
                  severity={
                    ctxTokens > CONTEXT_TOKEN_LIMIT ? "warning" : "info"
                  }
                  sx={{ mb: 1 }}
                >
                  Context tokens: {ctxTokens} / {CONTEXT_TOKEN_LIMIT}
                </Alert>

                {/* Constrained lower area to consume remaining vertical space */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    display: "grid",
                    gridTemplateRows: "minmax(0,1fr) auto",
                    gap: 1,
                  }}
                >
                  <TextField
                    label="Guidelines / Persona"
                    placeholder="How should this agent behave, speak, or specialize?"
                    multiline
                    rows={1}
                    value={guidelines}
                    onChange={(e) => setGuidelines(e.target.value)}
                    fullWidth
                    sx={{
                      marginTop: "10px",
                      height: "100%",
                      "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
                        height: "100% !important",
                        alignItems: "stretch",
                      },
                      "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
                        height: "100% !important",
                      },
                      "& .MuiInputBase-inputMultiline, & .MuiOutlinedInput-inputMultiline":
                        {
                          height: "100% !important",
                          overflow: "auto",
                          resize: "none",
                        },
                      "& textarea": {
                        height: "100% !important",
                        overflow: "auto",
                        resize: "none",
                      },
                    }}
                  />

                  {/* <TextField label="Forbidden Topics" placeholder="Topics this agent must avoid..." multiline minRows={2} maxRows={5} value={forbiddenTopics} onChange={(e) => setForbiddenTopics(e.target.value)} fullWidth /> */}
                  {/* Actions row (sticks to bottom) */}
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ mt: 1 }}
                  >
                    <Button
                      onClick={() => navigate("/app/agents")}
                      disabled={saving}
                    >
                      {translate("Cancel") || "Cancel"}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={!canSave}
                      startIcon={
                        saving ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: "inherit" }}
                          />
                        ) : null
                      }
                    >
                      {translate("Save") || "Save"}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Modals */}
      <AgentConversationsDialog
        open={showConvos}
        onClose={() => setShowConvos(false)}
        agent={{ id: agentId, ...(agent || {}) }}
        onMarkSeen={markConversationSeen}
      />
      <AgentTestDialog
        open={showTest}
        onClose={() => setShowTest(false)}
        agent={{ id: agentId, ...(agent || {}) }}
        tenantId={tenantId}
      />
      <AgentIntegrationsDialog
        open={showIntegrations}
        onClose={() => setShowIntegrations(false)}
        agent={{ id: agentId, ...(agent || {}) }}
        tenantId={tenantId}
      />

      {/* Monthly usage history dialog */}
      <Dialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {translate("MonthlyUsage") || "Monthly usage"}
          </Typography>
        </Box>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                {translate("Loading") || "Loading…"}
              </Typography>
            </Box>
          ) : monthlyHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {translate("NoHistory") || "No historical usage"}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {monthlyHistory.map((m) => (
                <Box
                  key={m.month}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.75,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatMonthLabel(m.month)}
                  </Typography>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography>
                      {new Intl.NumberFormat().format(
                        Math.round(m.totalTokens)
                      )}{" "}
                      {translate("Tokens") || "tokens"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      <span style={{ fontWeight: 700 }}>
                        {Math.round(
                          Math.min(100, (m.costUsd / monthlyBudgetUsd) * 100)
                        )}
                        %
                      </span>{" "}
                      {translate("OfMonthlyBudget") || "of monthly budget"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => setShowHistory(false)} variant="contained">
            {translate("Close") || "Close"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
